import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PhotoCamera as PhotoCameraIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, SERVICE_LEAK_TEST_STORE, EMPLOYEE_STORE, TEMPS_STORE } from '../../../config/constants';
import useDictation from '../../../hooks/useDictation';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import serviceLeakTestService from '../../../services/serviceLeakTestService';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

export default function ServiceLeakTestForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);

    // Get current user info
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        date: '',
        testItems: [{
            itemNo: 1,
            testerName: userFullName,
            testerId: userId,
            testerType: 'employee',
            dwgNo: '',
            jointNo: '',
            testMedium: '',
            passFail: 'Pass',
            customersRepresentative: '',
            pictures: []
        }],
        signatureType: 'internal',
        internalEmployeeId: userId,
        internalEmployeeName: userFullName,
        internalSignature: '',
        internalSignatureDate: dayjs().format('YYYY-MM-DD'),
        clientName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        comments: '',
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [signatureEmployeeModalOpen, setSignatureEmployeeModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [isDictating, setIsDictating] = useState(false);
    const [cameraTarget, setCameraTarget] = useState(null);
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Dictation for comments
    const { isSupported: speechSupported, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Combine employees and temps for tester selection
    const allPersonnel = useMemo(() => {
        const employeeList = employees.map(emp => ({
            id: emp.id,
            FullName: emp.FullName || emp.name || '',
            name: emp.FullName || emp.name || '',
            type: 'employee'
        }));
        const tempList = temps.map(temp => ({
            id: temp.id,
            FullName: `${temp.FullName || temp.name || ''} (Temp)`,
            name: temp.FullName || temp.name || '',
            type: 'temp'
        }));
        return [...employeeList, ...tempList];
    }, [employees, temps]);

    // Load jobs, employees and temps from IndexedDB
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();

                // Load jobs
                if (db.objectStoreNames.contains(JOB_STORE)) {
                    const tx = db.transaction(JOB_STORE, 'readonly');
                    const store = tx.objectStore(JOB_STORE);
                    const allJobs = await store.getAll();
                    const jobOptions = (allJobs || []).map(j => ({
                        ...j,
                        label: `${j.Job || ''} - ${j.JobName || ''}`
                    }));
                    setJobs(jobOptions);
                }

                // Load employees
                if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const store = tx.objectStore(EMPLOYEE_STORE);
                    const allEmployees = await store.getAll();
                    setEmployees(allEmployees || []);
                }

                // Load temps
                if (db.objectStoreNames.contains(TEMPS_STORE)) {
                    const tx = db.transaction(TEMPS_STORE, 'readonly');
                    const store = tx.objectStore(TEMPS_STORE);
                    const allTemps = await store.getAll();
                    setTemps(allTemps || []);
                }
            } catch (e) {
                console.error('Error loading data:', e);
            }
        })();
    }, []);

    // Load existing draft by uuid if provided
    useEffect(() => {
        const uuid = searchParams.get('uuid');
        if (uuid) {
            loadDraft(uuid);
        }
    }, [searchParams]);

    const loadDraft = async (uuid) => {
        try {
            const draft = await serviceLeakTestService.loadByUuid(uuid);
            if (draft) {
                // If form is already submitted, redirect to list
                if (draft.status === STATUS_SUBMITTED) {
                    navigate('/piping/forms/service-leak-test/list');
                    return;
                }
                setForm(draft);
                setSubmitted(false);
            }
        } catch (e) {
            console.error('Error loading draft:', e);
        }
    };

    // Auto-save function
    const queueSave = (data) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await serviceLeakTestService.saveDraft(data);
                console.log('Draft saved:', data.uuid);
            } catch (e) {
                console.error('Error saving draft:', e);
            }
        }, 1000);
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (newValue) {
            const updated = {
                ...form,
                jobCode: newValue.Job || '',
                jobName: newValue.JobName || '',
                customer: newValue.Customer || '',
                location: newValue.location || newValue.Location || ''
            };
            setForm(updated);
            queueSave(updated);
        } else {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
        }
    };

    // Handle item field changes
    const handleItemChange = (index, field, value) => {
        const updated = { ...form };
        updated.testItems[index][field] = value;
        setForm(updated);
        queueSave(updated);
    };

    // Handle tester selection for specific item
    const handleSelectTester = (person) => {
        if (selectedItemIndex !== null) {
            const updated = { ...form };
            updated.testItems[selectedItemIndex].testerName = person.FullName || person.name || '';
            updated.testItems[selectedItemIndex].testerId = person.id || '';
            updated.testItems[selectedItemIndex].testerType = person.type || 'employee';
            setForm(updated);
            queueSave(updated);
        }
        setEmployeeModalOpen(false);
        setSelectedItemIndex(null);
    };

    // Handle signature type change
    const handleSignatureTypeChange = (event) => {
        const newType = event.target.value;
        const updated = {
            ...form,
            signatureType: newType
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle signature accept
    const handleSignatureAccept = () => {
        if (!sigPadRef.current) {
            console.error('Signature pad ref not found');
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const dateField = form.signatureType === 'internal' ? 'internalSignatureDate' : 'clientSignatureDate';
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: dataUrl,
            [dateField]: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSelectInternalEmployee = (person) => {
        const updated = {
            ...form,
            internalEmployeeId: person.id || '',
            internalEmployeeName: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setSignatureEmployeeModalOpen(false);
    };

    // Add new test item
    const handleAddItem = () => {
        const newItem = {
            itemNo: form.testItems.length + 1,
            testerName: userFullName,
            testerId: userId,
            testerType: 'employee',
            dwgNo: '',
            jointNo: '',
            testMedium: '',
            passFail: 'Pass',
            customersRepresentative: '',
            pictures: []
        };
        const updated = { ...form, testItems: [...form.testItems, newItem] };
        setForm(updated);
        queueSave(updated);
    };

    // Delete test item
    const handleDeleteItem = (index) => {
        if (form.testItems.length === 1) {
            alert('Cannot delete the last item');
            return;
        }
        const updated = { ...form, testItems: form.testItems.filter((_, i) => i !== index) };
        // Renumber items
        updated.testItems = updated.testItems.map((item, i) => ({ ...item, itemNo: i + 1 }));
        setForm(updated);
        queueSave(updated);
    };

    // Duplicate test item
    const handleDuplicateItem = (index) => {
        const item = form.testItems[index];
        const newItem = {
            ...item,
            itemNo: form.testItems.length + 1,
            pictures: []
        };
        const updated = { ...form, testItems: [...form.testItems, newItem] };
        setForm(updated);
        queueSave(updated);
    };

    // Handle camera capture
    const handleCameraCapture = (photoData) => {
        if (cameraTarget && selectedItemIndex !== null) {
            const updated = { ...form };
            const item = updated.testItems[selectedItemIndex];
            const newPicture = {
                id: generateUUID(),
                dataUrl: photoData.dataUrl,
                name: photoData.name || `photo_${item.itemNo}_${(item.pictures.length + 1)}.jpg`,
                timestamp: photoData.timestamp || new Date().toISOString()
            };
            item.pictures.push(newPicture);
            setForm(updated);
            queueSave(updated);
        }
        setCameraTarget(null);
        setSelectedItemIndex(null);
    };

    // Handle file upload
    const handleFileUpload = async (e, itemIndex) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const updated = { ...form };
        const item = updated.testItems[itemIndex];

        for (const file of files) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const newPicture = {
                    id: generateUUID(),
                    dataUrl: event.target.result,
                    name: file.name,
                    timestamp: new Date().toISOString()
                };
                item.pictures.push(newPicture);
                setForm({ ...updated });
                queueSave({ ...updated });
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle dictation for comments
    const handleStartDictation = () => {
        setIsDictating(true);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm.comments || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        comments: newText
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        stopDictation();
        setIsDictating(false);
    };

    // Handle delete picture
    const handleDeletePicture = (itemIndex, pictureId) => {
        const updated = { ...form };
        updated.testItems[itemIndex].pictures = updated.testItems[itemIndex].pictures.filter(p => p.id !== pictureId);
        setForm(updated);
        queueSave(updated);
    };

    // Form validation
    const validate = () => {
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.date) newErrors.date = 'Date is required';

        if (form.signatureType === 'internal') {
            if (!form.internalSignature) newErrors.internalSignature = 'Signature is required';
            if (!form.internalEmployeeName) newErrors.internalEmployeeName = 'Employee name is required';
        } else {
            if (!form.clientSignature) newErrors.clientSignature = 'Signature is required';
            if (!form.clientName) newErrors.clientName = 'Client/Inspector name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit form
    const handleSubmit = async () => {
        if (!validate()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                confirmButtonColor: '#ff9800'
            });
            return;
        }

        setSaving(true);
        try {
            const result = await serviceLeakTestService.submit(form);

            if (result.success) {
                setForm({ ...form, status: STATUS_SUBMITTED, updatedAt: new Date().toISOString() });
                setSubmitted(true);
                setSaving(false);

                const swalResult = await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: result.message || 'Service Leak Test form submitted successfully!',
                    confirmButtonColor: '#4caf50'
                });

                if (swalResult.isConfirmed || swalResult.isDismissed) {
                    navigate('/piping/forms/service-leak-test/list');
                }
            } else {
                throw new Error(result.message || 'Failed to submit form');
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSaving(false);
            await Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                text: error.message || 'An error occurred while submitting the form',
                confirmButtonColor: '#f44336'
            });
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/piping/forms/service-leak-test/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Service Leak Test Report</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : 'Draft'}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                            color: 'white'
                        }}
                    />
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Job Information */}
                    <Accordion defaultExpanded sx={{ mb: 0.5 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                        onChange={handleJobChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Job *"
                                                error={!!errors.jobCode}
                                                helperText={errors.jobCode}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Date *"
                                        type="date"
                                        value={form.date}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                        onChange={(e) => handleFieldChange('date', e.target.value)}
                                        disabled={submitted}
                                        error={!!errors.date}
                                        helperText={errors.date}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            style: { cursor: submitted ? 'default' : 'pointer' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Test Items */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Test Items</Typography>
                            </Box>
                            {form.testItems.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No test items added yet. Click "Add Item" to get started.
                                </Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                {form.testItems.map((item, index) => (
                                    <Card key={index} variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    Item #{item.itemNo}
                                                </Typography>
                                                {!submitted && (
                                                    <Box display="flex" gap={1.5}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDuplicateItem(index)}
                                                            color="primary"
                                                            title="Duplicate item"
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteItem(index)}
                                                            color="error"
                                                            title="Delete item"
                                                            disabled={form.testItems.length === 1}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Tester Name"
                                            value={item.testerName}
                                            onClick={() => {
                                                if (!submitted) {
                                                    setSelectedItemIndex(index);
                                                    setEmployeeModalOpen(true);
                                                }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => {
                                                                setSelectedItemIndex(index);
                                                                setEmployeeModalOpen(true);
                                                            }}
                                                            size="small"
                                                        >
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="DWG#"
                                            value={item.dwgNo}
                                            onChange={(e) => handleItemChange(index, 'dwgNo', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Joint#"
                                            value={item.jointNo}
                                            onChange={(e) => handleItemChange(index, 'jointNo', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Test Medium"
                                            value={item.testMedium}
                                            onChange={(e) => handleItemChange(index, 'testMedium', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <ToggleButtonGroup
                                            value={item.passFail}
                                            exclusive
                                            onChange={(e, newValue) => newValue && handleItemChange(index, 'passFail', newValue)}
                                            disabled={submitted}
                                            size="small"
                                            fullWidth
                                        >
                                            <ToggleButton
                                                value="Pass"
                                                sx={{
                                                    backgroundColor: item.passFail === 'Pass' ? '#4caf50' : undefined,
                                                    color: item.passFail === 'Pass' ? 'white' : undefined,
                                                    '&:hover': { backgroundColor: item.passFail === 'Pass' ? '#45a049' : undefined }
                                                }}
                                            >
                                                Pass
                                            </ToggleButton>
                                            <ToggleButton
                                                value="Fail"
                                                sx={{
                                                    backgroundColor: item.passFail === 'Fail' ? '#f44336' : undefined,
                                                    color: item.passFail === 'Fail' ? 'white' : undefined,
                                                    '&:hover': { backgroundColor: item.passFail === 'Fail' ? '#e53935' : undefined }
                                                }}
                                            >
                                                Fail
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer's Representative"
                                            value={item.customersRepresentative}
                                            onChange={(e) => handleItemChange(index, 'customersRepresentative', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>

                                    {/* Pictures */}
                                    <Grid item xs={12}>
                                        <Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Pictures ({item.pictures.length})
                                                </Typography>
                                                {!submitted && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<PhotoCameraIcon />}
                                                        onClick={() => {
                                                            setSelectedItemIndex(index);
                                                            setCameraTarget('picture');
                                                        }}
                                                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                    >
                                                        Add Photo
                                                    </Button>
                                                )}
                                            </Box>
                                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                                {item.pictures.map((pic) => (
                                                    <Box key={pic.id} position="relative" display="inline-block">
                                                        <img
                                                            src={pic.dataUrl}
                                                            alt={pic.name}
                                                            style={{
                                                                width: 60,
                                                                height: 60,
                                                                objectFit: 'cover',
                                                                borderRadius: 4,
                                                                border: '1px solid #ddd'
                                                            }}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeletePicture(index, pic.id)}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: -6,
                                                                    right: -6,
                                                                    bgcolor: 'error.main',
                                                                    color: 'white',
                                                                    p: 0.25,
                                                                    '&:hover': { bgcolor: 'error.dark' }
                                                                }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                                {!submitted && form.testItems.length > 0 && (
                                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddItem}
                                            size="small"
                                        >
                                            Add Item
                                        </Button>
                                    </Box>
                                )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    {/* Comments */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>Comments</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                size="small"
                                placeholder="Enter any additional comments or notes here..."
                                value={form.comments}
                                onChange={(e) => handleFieldChange('comments', e.target.value)}
                                disabled={submitted}
                                InputProps={{
                                    endAdornment: !submitted && speechSupported && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    isDictating
                                                        ? handleStopDictation()
                                                        : handleStartDictation()
                                                }
                                                color={isDictating ? 'error' : 'default'}
                                            >
                                                {isDictating ? <MicOffIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={form.signatureType}
                                    exclusive
                                    onChange={(e, newValue) => newValue && handleSignatureTypeChange({ target: { value: newValue } })}
                                    disabled={submitted}
                                    size="small"
                                >
                                    <ToggleButton value="internal" sx={{ px: 2 }}>
                                        Internal
                                    </ToggleButton>
                                    <ToggleButton value="client" sx={{ px: 2 }}>
                                        Client/Inspector
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {form.signatureType === 'internal' ? (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Employee Name *"
                                                value={form.internalEmployeeName}
                                                onClick={() => !submitted && setSignatureEmployeeModalOpen(true)}
                                                InputProps={{
                                                    readOnly: true,
                                                    style: { cursor: submitted ? 'default' : 'pointer' },
                                                    endAdornment: !submitted && (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setSignatureEmployeeModalOpen(true)} size="small">
                                                                <PersonSearchIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.internalEmployeeName}
                                                helperText={errors.internalEmployeeName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.internalSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('internalSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.internalSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.internalSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.internalSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.internalSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Client/Inspector Name *"
                                                value={form.clientName}
                                                onChange={(e) => handleFieldChange('clientName', e.target.value)}
                                                disabled={submitted}
                                                error={!!errors.clientName}
                                                helperText={errors.clientName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.clientSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.clientSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.clientSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.clientSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.clientSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectTester}
                employees={allPersonnel}
            />

            <SelectSingleEmployeeModal
                open={signatureEmployeeModalOpen}
                onClose={() => setSignatureEmployeeModalOpen(false)}
                onSelect={handleSelectInternalEmployee}
                employees={employees}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.signatureType === 'internal' ? form.internalEmployeeName : form.clientName}
                onAccept={handleSignatureAccept}
                existingSignature={form.signatureType === 'internal' ? form.internalSignature : form.clientSignature}
            />

            <CameraCapture
                open={!!cameraTarget && selectedItemIndex !== null}
                onClose={() => {
                    setCameraTarget(null);
                    setSelectedItemIndex(null);
                }}
                onCapture={handleCameraCapture}
            />

            <Backdrop open={saving} sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
}
